/**
 * dview:SpotViewer (board 2): an object placed at a km on a track, among its neighbours on that track.
 * It lines them up in km order with this one on its side of the line; its links are the hero's
 * shared Graph tab.
 * The row is a table of what sits on the same track; dview:track and dview:at name the rows with the
 * track and the km, dview:around how many neighbours either side.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@bn-apps/dim-ui-react';
import { conceptOf, type Concept } from '@services/vocabulary.service';
import { expandUri, uriToDereferencePath } from '@utils/prefix.utils';
import {
  pickLang,
  setting,
  settingList,
  type ViewPropertyShape,
} from '@utils/viewShapes.utils';
import { keyedValues } from '../page';
import { formatKm, localName, num, roleMap, words } from '../linearLocation';
import { Glyph, ticksFor } from './alongTrack';
import { FitGlyph, Legend, LegendToggle } from './legend';
import { useStored } from '../stored';
import { useNumberFormat } from './figures';
import { firstKm } from './line';
import { signValue } from './signValue';
import { TjnPicture } from './tjn';
import { uriText } from './values';
import type { Widget, WidgetContext } from './types';
import styles from './spot.module.scss';

interface Node {
  iri: string;
  name: string;
  className: string;
  /** The class that picks its picture and its kind. */
  type: string;
  /** Its dview:kindBy value (a signal's railML type): what "previous" and "next" compare. */
  kind: string | null;
  /** Its TJN concept's dview:family (main-signal for 8.10 to 8.13): a narrower kind than the railML type. */
  family: string | null;
  /** The way it faces on the track (the table's facing column), for this object's previous and next. */
  facing: string | null;
  symbol: string;
  /** Its TJN code (the type designator entry), for its drawing. */
  code: string | null;
  /** What its TJN sign writes. */
  value: string | undefined;
  km: number | null;
  /** In the graph: how it relates to this object. */
  relation: string | null;
}

/** The label of a class from the shapes (a node shape targeting it), else its words. */
function classLabel(classIri: string, context: WidgetContext): string {
  const shape = context.model?.shapes?.nodeShapes.find((node) =>
    node.targetClasses.includes(classIri)
  );
  const label = shape ? pickLang(shape.label, context.language) : null;
  return label ?? words(localName(classIri));
}

/** Class → symbol, from every row in the shapes that draws a class (sh:class + dview:symbol). */
function symbolsByClass(context: WidgetContext): Record<string, string> {
  const rows: ViewPropertyShape[] =
    context.model?.shapes?.nodeShapes.flatMap((node) => node.properties) ?? [];
  return Object.fromEntries(
    rows
      .filter((row) => row.classIri && setting(row, 'symbol'))
      .map((row) => [row.classIri!, setting(row, 'symbol')!])
  );
}

type Item = { cells: Record<string, { value: string }[] | undefined> };

/** A table item's concept by its dview:symbolBy column (a signal's TJN paragraph). */
export function neighbourConcept(item: Item, table: ViewPropertyShape, context: WidgetContext): Concept | null {
  const by = setting(table, 'symbolBy');
  const column =
    by && table.nodeShapeId
      ? context.model?.shapes?.nodeShapesById
          .get(table.nodeShapeId)
          ?.properties.find((row) => row.key === by)
      : undefined;
  return column ? conceptOf(context.concepts, setting(column, 'scheme'), item.cells[by!]?.[0]?.value) : null;
}

/** A table item's picture: its dview:symbolBy column's concept (a signal's TJN paragraph), else the symbol of one of its classes. */
export function neighbourSymbol(item: Item, table: ViewPropertyShape, context: WidgetContext): string {
  const concept = neighbourConcept(item, table, context);
  if (concept?.symbol) return concept.symbol;
  const symbols = symbolsByClass(context);
  return (item.cells.type ?? []).map((value) => symbols[value.value]).find(Boolean) ?? 'point';
}

/**
 * Signals and signs are both railml3:SignalIS: a real signal has a picture of its own (main-signal,
 * distant-signal) from its TJN paragraph, a sign only the class's plain "signal".
 */
export const symbolFamily = (symbol: string) => (symbol.endsWith('-signal') ? 'real-signal' : symbol);

const middle = (from: number | null, to: number | null) =>
  from !== null && to !== null ? (from + to) / 2 : null;

/** Label lanes on one side of the track: each name takes the nearest lane where it does not touch another. */
function laneOf(lanes: [number, number][][], left: number, right: number): number {
  for (let lane = 0; lane < lanes.length; lane++) {
    if (lanes[lane].every(([a, b]) => right + 8 < a || left - 8 > b)) {
      lanes[lane].push([left, right]);
      return lane;
    }
  }
  return -1;
}

const LANES = 3;
// More metres than this between two objects on a track record: the far ones are on another stretch
const GAP = 2000;
const LINE_Y = 170;
const LANE_HEIGHT = 18;
const GLYPH_HEIGHT = 22;

const Spot = ({ property, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const format = useNumberFormat();
  const [legend, setLegend] = useStored('dim-entity-legend', false);
  const [picked, setPicked] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  /** The km range zoomed to on the strip (drag over it, or Ctrl + wheel), as on the Track hero. */
  const [view, setView] = useState<[number, number] | null>(null);
  const [drag, setDrag] = useState<[number, number] | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const wheel = useRef<(event: WheelEvent) => void>(() => undefined);
  // Positions are in pixels of the stage, so they stay right when the window changes size
  const [width, setWidth] = useState(0);
  useEffect(() => {
    // React's wheel listener is passive and cannot stop the page zooming
    const stage = stageRef.current;
    if (!stage) return;
    const listener = (event: WheelEvent) => wheel.current(event);
    stage.addEventListener('wheel', listener, { passive: false });
    const observer = new ResizeObserver(() => setWidth(stage.clientWidth));
    observer.observe(stage);
    setWidth(stage.clientWidth);
    return () => {
      stage.removeEventListener('wheel', listener);
      observer.disconnect();
    };
  }, []);
  const model = context.model;
  const table = model?.tables[property.id];
  // Its km: dview:at, else the middle of dview:kmFrom and dview:kmTo (a bridge, a tunnel); each names its rows in order of preference
  const here =
    firstKm(context, property, 'at') ??
    middle(firstKm(context, property, 'kmFrom'), firstKm(context, property, 'kmTo'));
  const trackRow = model?.rows[setting(property, 'track') ?? ''];
  const track = trackRow
    ? model?.values[trackRow.id]?.find((value) => value.type === 'uri')?.value
    : undefined;
  if (!model || !table || here === null || !track) return null;

  const symbols = symbolsByClass(context);
  const except = new Set(
    settingList(property, 'except').map((name) => expandUri(name, context.prefixes))
  );
  // The most specific class: one with a symbol, else the shaped class the fewest neighbours share
  // (superclasses such as rtm:NetEntity are on all of them)
  const share = new Map<string, number>();
  for (const item of table.items)
    for (const value of item.cells.type ?? [])
      share.set(value.value, (share.get(value.value) ?? 0) + 1);
  const shaped = (type: string) =>
    Boolean(context.model?.shapes?.nodeShapes.some((node) => node.targetClasses.includes(type)));
  const mainClass = (types: string[]) =>
    types.find((type) => symbols[type]) ??
    [...types].filter(shaped).sort((a, b) => (share.get(a) ?? 0) - (share.get(b) ?? 0))[0] ??
    types[0];
  // This object's own picture: the concept of a row with dview:scheme (its TJN paragraph), else dview:symbolFor
  const ownConcept = model.allProperties
    .map((row) => {
      const value = model.values[row.id]?.[0]?.value;
      const concept = conceptOf(context.concepts, setting(row, 'scheme'), value);
      return concept && value ? { concept, code: value } : null;
    })
    .find(Boolean) as { concept: Concept; code: string } | undefined;
  const ownSymbol =
    ownConcept?.concept.symbol ??
    model.allProperties
      .map((row) => roleMap(row, 'symbolFor')(model.values[row.id]?.[0]?.value))
      .find(Boolean);

  // dview:symbolBy names the column whose value picks the picture (a signal's TJN paragraph)
  const by = setting(property, 'symbolBy');
  const byColumn =
    by && property.nodeShapeId
      ? model.shapes?.nodeShapesById
          .get(property.nodeShapeId)
          ?.properties.find((row) => row.key === by)
      : undefined;
  const conceptBy = (item: (typeof table.items)[number]) =>
    byColumn ? conceptOf(context.concepts, setting(byColumn, 'scheme'), item.cells[by!]?.[0]?.value) : null;
  const symbolBy = (item: (typeof table.items)[number]) => conceptBy(item)?.symbol ?? null;

  const kindBy = setting(property, 'kindBy');
  const all: Node[] = table.items
    // The track itself and the classes dview:except names (tracks, lines on it) are not neighbours
    .filter(
      (item) => item.iri !== track && !item.cells.type?.some((value) => except.has(value.value))
    )
    .map((item) => {
      const types = (item.cells.type ?? []).map((value) => value.value);
      const type = mainClass(types) ?? '';
      const className = type ? classLabel(type, context) : '';
      const label = uriText(item.iri, context);
      // The km on this track from the topology (its associated track), else bno:atKm or km from/to
      const km =
        item.iri === model.uri
          ? here
          : (num(item.cells['track-at']) ??
            middle(num(item.cells['track-from']), num(item.cells['track-to'])) ??
            num(item.cells.at) ??
            middle(num(item.cells.from), num(item.cells.to)));
      // Without a label of its own, an object reads as its class at its km
      const unnamed = !context.resolved[item.iri] && item.iri !== model.uri;
      const code = item.iri === model.uri ? (ownConcept?.code ?? null) : by ? (item.cells[by]?.[0]?.value ?? null) : null;
      return {
        iri: item.iri,
        name:
          unnamed && km !== null
            ? `${className || t('entity.template.spot.untyped')} · km ${formatKm(km)}`
            : label,
        className,
        type,
        kind: kindBy ? (item.cells[kindBy]?.[0]?.value ?? null) : null,
        family: item.iri === model.uri ? (ownConcept?.concept.family ?? null) : (conceptBy(item)?.family ?? null),
        facing: item.iri === model.uri ? null : (item.cells.facing?.[0]?.value ?? null),
        symbol:
          item.iri === model.uri
            ? (ownSymbol ?? symbols[type] ?? 'point')
            : (symbolBy(item) ?? symbols[type] ?? 'point'),
        code,
        value: signValue(code, { label, km: num(item.cells.at) ?? km }),
        km,
        relation: null,
      };
    })
    .filter((node) => node.km !== null)
    .sort((a, b) => a.km! - b.km!);
  // Bare spot locations without a type of their own clutter the strip
  for (let i = all.length - 1; i >= 0; i--) if (!all[i].className && all[i].iri !== model.uri) all.splice(i, 1);
  const me = all.findIndex((node) => node.iri === model.uri);
  if (me < 0) return null;
  const around = Number(setting(property, 'around') ?? 4);
  const strip = all.slice(Math.max(0, me - around), me + around + 1);
  const self = all[me];
  // Which way it faces along the track (dview:direction names the row): normal is towards higher km
  const facing = keyedValues(model, setting(property, 'direction') ?? '')[0]?.value;
  // Which side of the track it stands on, looking towards higher km: left above the line, right below
  const side = keyedValues(model, setting(property, 'side') ?? '')[0]?.value;

  // The strip is a km scale: dview:window metres either side of it (inside the track record), else the
  // neighbours around it, or the range zoomed to
  const near = strip.map((node) => node.km!);
  const pad = Math.max(30, (Math.max(...near) - Math.min(...near)) * 0.08);
  // The track record's extent: the objects in one run around it; a km from another stretch lies far off
  const kms = all.map((node) => node.km!);
  let first = me;
  let last = me;
  while (first > 0 && kms[first] - kms[first - 1] <= GAP) first--;
  while (last < kms.length - 1 && kms[last + 1] - kms[last] <= GAP) last++;
  // At least 100 m either side, so a lone object does not get a strip a few metres wide
  const trackLo = Math.min(kms[first] - 30, self.km! - 100);
  const trackHi = Math.max(kms[last] + 30, self.km! + 100);
  const windowMetres = Number(setting(property, 'window')) || 0;
  // At least 500 m around it, so objects at the same spot (a balise group under a signal) do not shrink the view
  // The window always: near the end of a track record its far side is simply empty
  const lo = windowMetres ? self.km! - windowMetres : Math.min(Math.min(...near) - pad, self.km! - 250);
  const hi = windowMetres ? self.km! + windowMetres : Math.max(Math.max(...near) + pad, self.km! + 250);
  const [from, to] = view ?? [lo, hi];
  const wholeTrack = !view && windowMetres > 0 && (lo > trackLo || hi < trackHi);
  const onStrip = all.filter((node) => node.km! >= from && node.km! <= to);
  const kindOf = (node: Node) => node.className || t('entity.template.spot.untyped');
  const kinds = [
    ...onStrip
      .filter((node) => node.iri !== self.iri)
      .reduce((byKind, node) => {
        const entry = byKind.get(kindOf(node)) ?? { symbol: node.symbol, iris: new Set<string>() };
        entry.iris.add(node.iri);
        return byKind.set(kindOf(node), entry);
      }, new Map<string, { symbol: string; iris: Set<string> }>()),
  ]
    .map(([name, entry]) => ({ name, symbol: entry.symbol, count: entry.iris.size }))
    .sort((a, b) => b.count - a.count);
  const kmAt = (clientX: number) => {
    const box = stageRef.current!.getBoundingClientRect();
    return from + ((clientX - box.left - 24) / Math.max(1, box.width - 48)) * (to - from);
  };
  const zoomTo = (a: number, b: number) => {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    if (hi - lo >= 10) setView([lo, hi]);
  };
  wheel.current = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const at = kmAt(event.clientX);
    const factor = event.deltaY > 0 ? 1.4 : 1 / 1.4;
    zoomTo(at - (at - from) * factor, at + (to - at) * factor);
  };
  // The strip, in pixels: this object on its side of the track with its name beside it, the
  // neighbours on the line with their names in lanes on the other side, never overlapping
  const stageWidth = width || 800;
  const px = (km: number) => 24 + ((km - from) / (to - from)) * (stageWidth - 48);
  const selfX = px(self.km!);
  // One short line under its name; the Location card has the full sentences
  const facingText =
    facing === 'normal' || facing === 'reverse'
      ? `${facing === 'normal' ? '→' : '←'} ${t(`entity.template.symbol.direction.${facing}Short`)}`
      : null;
  const sideText = side ? t(`entity.template.symbol.side.${side}Short`, '') : '';
  const selfMeta = [facingText, sideText].filter(Boolean).join(' · ');
  const selfLabelWidth = Math.max(self.name.length * 8, selfMeta.length * 6.5) + 32;
  const selfLabelLeft = selfX + selfLabelWidth > stageWidth;
  const lanes: [number, number][][] = Array.from({ length: LANES }, () => []);
  // This object's picture and name take the two lanes nearest the track around it
  const selfSpan: [number, number] = selfLabelLeft
    ? [selfX - selfLabelWidth, selfX + 20]
    : [selfX - 20, selfX + selfLabelWidth];
  lanes[0].push(selfSpan);
  lanes[1].push(selfSpan);
  const others = onStrip
    .filter((node) => node.iri !== self.iri)
    .map((node) => ({ node, x: px(node.km!) }));
  // Only this object and the previous and next of its own kind on this track are named; the rest name
  // themselves on hover. Same kind: its TJN family, else its dview:kindBy value, else its family of picture,
  // facing the same way. Its twins at the same km (the parts of a combined signal) are not before or after it
  const directed = (way: string | null | undefined) => way === 'normal' || way === 'reverse';
  const same = all.slice(first, last + 1).filter(
    (node) =>
      node.iri === self.iri ||
      (node.type === self.type &&
        (self.family
          ? node.family === self.family
          : self.kind
            ? node.kind === self.kind
            : symbolFamily(node.symbol) === symbolFamily(self.symbol)) &&
        (!directed(facing) || !directed(node.facing) || node.facing === facing) &&
        Math.abs(node.km! - self.km!) >= 1)
  );
  const mine = same.findIndex((node) => node.iri === self.iri);
  // Off the strip, the previous and next are named at its edge with how far away they are
  const beyond = [same[mine - 1], same[mine + 1]]
    .filter((node): node is Node => Boolean(node) && (node!.km! < from || node!.km! > to))
    .map((node) => ({ node, before: node.km! < self.km!, metres: Math.abs(node.km! - self.km!) }));
  const named = [same[mine - 1], same[mine + 1], others.find(({ node }) => node.iri === hovered)?.node]
    .filter((node): node is Node => Boolean(node) && node!.km! >= from && node!.km! <= to)
    .filter((node, index, list) => list.findIndex((other) => other.iri === node.iri) === index);
  const labels = named.map((node) => {
    const x = px(node.km!);
    const half = (node.name.length * 7 + 8) / 2;
    // At this object's own spot (its balise group, a twin) a name would cross its picture
    const lane = Math.abs(x - selfX) < 20 ? -1 : laneOf(lanes, x - half, x + half);
    return { x, text: node.name, lane, first: node.iri };
  });

  const selected =
    onStrip.find((node) => node.iri === picked) ?? self;
  const distance =
    selected.km !== null && selected.iri !== self.iri
      ? Math.round(Math.abs(selected.km - self.km!))
      : null;
  const relation =
    selected.iri === self.iri
      ? t('entity.template.spot.thisPage')
      : distance !== null
          ? t(
              selected.km! < self.km!
                ? 'entity.template.spot.before'
                : 'entity.template.spot.after',
              { metres: format(distance, 0) }
            )
          : (selected.relation ?? '');
  const pickedX = selected.km !== null ? px(selected.km) : selfX;
  const ticks = ticksFor(from, to, Math.max(3, Math.round(stageWidth / 160)));

  return (
    <div className={styles.spot}>
      <div className={styles.bar}>
        <span className={styles.barTools}>
          {view && (
            <Button variant="tertiary" size="xs" onClick={() => setView(null)}>
              {t('entity.template.along.resetZoom', { from: formatKm(from), to: formatKm(to) })}
            </Button>
          )}
          {wholeTrack && (
            <Button variant="tertiary" size="xs" onClick={() => setView([trackLo, trackHi])}>
              {t('entity.template.spot.wholeTrack', { from: formatKm(kms[first]), to: formatKm(kms[last]) })}
            </Button>
          )}
          {kinds.length > 0 && <LegendToggle open={legend} onToggle={setLegend} />}
        </span>
      </div>
      {/* What the marks mean, when asked for */}
      {legend && kinds.length > 0 && (
        <Legend
          label={t('entity.template.along.legend')}
          entries={kinds.map((entry) => ({
            key: entry.name,
            icon: <FitGlyph symbol={entry.symbol} />,
            label: entry.name,
            count: entry.count,
          }))}
        />
      )}
      <div
        ref={stageRef}
        className={styles.stage}
        onPointerDown={(event) =>
          event.button === 0 && setDrag([kmAt(event.clientX), kmAt(event.clientX)])
        }
        onPointerMove={(event) => drag && setDrag([drag[0], kmAt(event.clientX)])}
        onPointerUp={() => {
          if (drag && Math.abs(px(drag[1]) - px(drag[0])) > 4) zoomTo(drag[0], drag[1]);
          setDrag(null);
        }}
        onPointerLeave={() => setDrag(null)}
      >
          <>
            {drag && Math.abs(px(drag[1]) - px(drag[0])) > 4 && (
              <span
                className={styles.zoomBox}
                style={{
                  left: Math.min(px(drag[0]), px(drag[1])),
                  width: Math.abs(px(drag[1]) - px(drag[0])),
                }}
              />
            )}
            <span className={styles.trackLine} style={{ top: LINE_Y }} aria-hidden="true" />
            {beyond.map(({ node, before, metres }) => (
              <Link
                key={node.iri}
                to={uriToDereferencePath(node.iri)}
                className={styles.beyond}
                data-before={before || undefined}
                style={{ top: LINE_Y + 8 }}
              >
                {before ? '← ' : ''}
                {node.name} · {metres >= 1000 ? `${format(metres / 1000, 1)} km` : `${format(metres, 0)} m`}
                {before ? '' : ' →'}
              </Link>
            ))}
            <span
              className={styles.trackSpan}
              style={{
                top: LINE_Y - 1,
                left: Math.min(selfX, pickedX),
                width: Math.abs(pickedX - selfX),
              }}
              aria-hidden="true"
            />
            {ticks.map((tick) => (
              <span key={tick} className={styles.tick} style={{ left: px(tick) }}>
                {formatKm(tick)}
              </span>
            ))}
            {others.map(({ node, x }) => (
              <button
                key={node.iri}
                type="button"
                className={styles.neighbour}
                style={{ left: x, top: LINE_Y }}
                aria-pressed={node.iri === selected.iri}
                aria-label={`${node.name} · ${node.className}`}
                title={`${node.name} · ${node.className}${node.km !== null ? ` · km ${formatKm(node.km)}` : ''}`}
                onClick={() => setPicked(node.iri)}
                onPointerEnter={() => setHovered(node.iri)}
                onPointerLeave={() => setHovered(null)}
                onFocus={() => setHovered(node.iri)}
                onBlur={() => setHovered(null)}
              >
                <svg
                  width="20"
                  height="22"
                  viewBox="0 0 28 30"
                  className={styles.glyph}
                  aria-hidden="true"
                >
                  <Glyph symbol={node.symbol} code={node.code} value={node.value} x={14} y={29} />
                </svg>
              </button>
            ))}
            {labels
              .filter((label) => label.lane >= 0)
              .map((label) => (
                <span
                  key={label.first}
                  className={styles.neighbourLabel}
                  style={{ left: label.x, top: LINE_Y }}
                  aria-hidden="true"
                >
                  {label.lane > 0 && (
                    <span
                      className={styles.leader}
                      style={{ blockSize: label.lane * LANE_HEIGHT + 4 }}
                    />
                  )}
                  <span
                    className={styles.neighbourName}
                    style={{ bottom: GLYPH_HEIGHT + 4 + label.lane * LANE_HEIGHT }}
                  >
                    {label.text}
                  </span>
                </span>
              ))}
            <button
              type="button"
              className={styles.self}
              style={{ left: selfX, top: LINE_Y }}
              aria-current="true"
              aria-pressed={self.iri === selected.iri}
              onClick={() => setPicked(self.iri)}
            >
              <span className={styles.pole} aria-hidden="true" />
              <span className={styles.selfPicture}>
                {ownConcept ? (
                  <TjnPicture
                    key={ownConcept.code}
                    concept={ownConcept.concept}
                    code={ownConcept.code}
                    value={self.value}
                    className={styles.tjn}
                  />
                ) : (
                  <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
                    <Glyph symbol={self.symbol} code={self.code} value={self.value} x={14} y={27} />
                  </svg>
                )}
              </span>
              <span className={styles.selfLabel} data-left={selfLabelLeft || undefined}>
                <span className={styles.selfName}>{self.name}</span>
                <span className={styles.selfMeta}>{selfMeta}</span>
              </span>
            </button>
          </>
      </div>
      {/* Under the drawing: what it does on the left, the object picked on it on the right */}
      <div className={styles.under}>
        <p className={styles.hint}>{t('entity.template.spot.hint')}</p>
        {selected.iri !== self.iri && (
          <span className={styles.footer}>
            <span className={styles.footerLabel}>{t('entity.template.spot.selected')}</span>
            <b>{selected.name}</b>
            <span className={styles.footerMeta}>
              {selected.className}
              {relation && ` · ${relation}`}
            </span>
            <Link to={uriToDereferencePath(selected.iri)} className={styles.go}>
              {t('entity.template.spot.go')}
            </Link>
          </span>
        )}
      </div>
    </div>
  );
};

export const SpotViewer: Widget = (props) => <Spot {...props} />;
